import { Link } from 'react-router-dom';
import OverflowMenu from '../../components/OverflowMenu';
import SafeImage from '../../components/SafeImage';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { SocialMoreIcon } from '../social-post/SocialActionIcons';
import { SOCIAL_ACTION_PRODUCT_CHROME as socialChrome } from '../social-post/socialActionPresentation';
import { canShowFeedPreferenceActions, FEED_CONTENT_TYPES } from './discoverModel';
import JourneyStoryEntry from './JourneyStoryEntry';
import { useContentVisibilityTelemetry } from './useContentVisibilityTelemetry';

/**
 * For You 的 Journey 推薦卡，以及 Social 投影缺席時的舊版 Social 卡。
 * 不顯示 engagement；偏好動作收在「更多」。
 */

export default function ForYouCard({
  item,
  onNotInterested,
  onMute,
  onHide,
  onFlushBehavior,
  onFlushImpression,
  isAuthenticated,
  viewerMemberId,
  feedSessionId,
}) {
  const { t } = useTravelTranslation();
  const isJourney = item.contentType === FEED_CONTENT_TYPES.JOURNEY_PUBLICATION;
  const href = isJourney
    ? `/public/journeys/${item.contentId}`
    : `/social/posts/${item.contentId}`;

  const kindLabel = isJourney
    ? t('travel.discover.contentType.journey')
    : t('travel.discover.contentType.socialPost');

  const showPreferenceActions = isAuthenticated && canShowFeedPreferenceActions({
    viewerMemberId,
    creatorMemberId: item.creatorMemberId,
  });

  const { ref } = useContentVisibilityTelemetry({
    enabled: true,
    contentType: item.contentType,
    contentId: item.contentId,
    feedSessionId,
    onFlushBehavior,
    onFlushImpression,
  });

  return (
    <article ref={ref} className="for-you-card" data-testid={`for-you-card-${item.itemKey}`}>
      <Link to={href} className="for-you-card__cover-link" aria-label={item.title} tabIndex={-1}>
        <SafeImage
          src={item.coverUrl}
          alt=""
          className="for-you-card__cover"
          loading="lazy"
          fallback={<div className="for-you-card__cover for-you-card__cover--fallback" aria-hidden="true" />}
        />
      </Link>
      <div className="for-you-card__body">
        <div className="for-you-card__header-row">
          <span className="for-you-card__kind">{kindLabel}</span>
          {item.deliveryType === 'SPONSORED' ? (
            <span className="for-you-card__sponsored-badge" data-testid="for-you-card-sponsored-badge">
              {t('travel.discover.sponsored')}
            </span>
          ) : null}
          {showPreferenceActions ? (
            <OverflowMenu
              items={[
                { id: 'not-interested', label: t('travel.discover.notInterested'), onSelect: () => onNotInterested?.(item) },
                { id: 'mute', label: t('travel.discover.mute'), onSelect: () => onMute?.(item) },
                { id: 'hide', label: t('travel.discover.hideContent'), onSelect: () => onHide?.(item) },
              ]}
              label={socialChrome.moreMenuAria}
              triggerContent={<SocialMoreIcon />}
              wrapClassName="for-you-card__more"
              triggerClassName="for-you-card__more-trigger"
              menuClassName="for-you-card__menu"
              triggerTestId="for-you-card-more"
              menuTestId="for-you-card-more-menu"
            />
          ) : null}
        </div>
        <Link to={href} className="for-you-card__title-link">
          <h3 className="for-you-card__title">{item.title}</h3>
        </Link>
        {item.summary ? <p className="for-you-card__summary">{item.summary}</p> : null}
        <div className="for-you-card__meta">
          {item.destinationLabel ? (
            <span className="for-you-card__destination" title={item.destinationLabel}>
              {item.destinationLabel}
            </span>
          ) : null}
          {item.dayCount != null ? (
            <span className="for-you-card__days">
              {item.dayCount} {t('travel.discover.days')}
            </span>
          ) : null}
          {item.budgetLabel ? (
            <span className="for-you-card__budget">{item.budgetLabel}</span>
          ) : null}
        </div>
        {isJourney ? (
          // 含贊助行程；與主要連結並列，不包在任何連結內
          <JourneyStoryEntry publicationId={item.contentId} title={item.title} storyCardCount={item.storyCardCount} />
        ) : null}
      </div>
    </article>
  );
}
